/* eslint-disable camelcase */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import Rating from '@material-ui/lab/Rating';
import CloseIcon from '@material-ui/icons/Close';

import get from 'lodash/get';
import classNames from 'classnames';

// app modules: services
import history from 'services/history';

// app: assets
import CssStarRating from './StarRating.scss';

const RETURN_TO_AGGREGATE_VIEW_DELAY = 1000;

export const GET_USER_RATING = gql`
  query GetUserRecipeRating($content_id: String!) {
    UserRecipeFeedback(content_id: $content_id) {
      id
      star_rating
    }
  }
`;
export const SAVE_RATING = gql`
  mutation SaveUserRecipeRating($content_id: String!, $content_hash: String, $star_rating: Float) {
    UserRecipeFeedback(
      content_id: $content_id
      content_hash: $content_hash
      star_rating: $star_rating
    ) {
      id
      star_rating
      ratings {
        average
        count
      }
    }
  }
`;

function StarRating(props) {
  const {
    averageRating,
    className,
    compact,
    contentHash,
    contentId,
    count,
    noText,
    readOnly,
    size,
    currentUserComment,
  } = props;
  useStyles(CssStarRating);
  const isTouch = useRef(false);
  const editCompleteTimer = useRef(null);
  const ratingsComponentRef = useRef(null);
  const [isActive, setActive] = useState(!averageRating);
  const [aggregateRating, setAggregateRating] = useState({ count, average: averageRating });
  const [ratingWasSaved, setRatingWasSaved] = useState(false);
  const [hover, setHover] = useState(false);
  const [unsavedValue, setUnsavedValue] = useState(null);
  const loggedIn = useSelector(state => state.user.logged_in);

  // Reset everything if the contentId changes
  useEffect(
    () => {
      if (!props.readOnly) {
        setActive(!averageRating);
        setAggregateRating({ count, average: averageRating });
        setRatingWasSaved(false);
        setUnsavedValue(null);
        editCompleteTimer.current = null;
      }
    },
    [contentId, averageRating, count, loggedIn],
  );

  const beginTransitionToAggregateView = force => {
    if (force || !hover) {
      clearTimeout(editCompleteTimer.current);
      editCompleteTimer.current = setTimeout(() => {
        setRatingWasSaved(false);
        setActive(false);
      }, RETURN_TO_AGGREGATE_VIEW_DELAY);
    }
  };

  const cancelTransitionToAggregateView = () => {
    clearTimeout(editCompleteTimer.current);
    editCompleteTimer.current = null;
  };

  const { data } = useQuery(GET_USER_RATING, {
    variables: {
      content_id: contentId,
    },
    skip: !loggedIn || readOnly || (!isActive && !hover && !ratingWasSaved),
    ssr: false,
  });

  const [saveRating, { loading: saveInProgress }] = useMutation(SAVE_RATING, {
    onCompleted({ UserRecipeFeedback }) {
      const { id, ratings } = UserRecipeFeedback;
      if (id !== 'temp-id') {
        // Save complete
        setRatingWasSaved(true);

        // Give the user a few seconds to admire their own rating before switching back to the aggregate view
        beginTransitionToAggregateView();
      } else {
        // A temp-id means that saving is in progress - this is the optimistic response
        setRatingWasSaved(false);
      }
      if (ratings) {
        // The API will return updated aggregate ratings.
        setAggregateRating(ratings);
      }
    },
    update(
      cache,
      {
        data: { UserRecipeFeedback },
      },
    ) {
      if (UserRecipeFeedback.id !== 'temp-id') {
        // Update the recipe cache with the new aggregate ratings
        cache.writeFragment({
          data: { ratings: UserRecipeFeedback.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${contentId}:full`,
        });
        cache.writeFragment({
          data: { ratings: UserRecipeFeedback.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${contentId}:preview`,
        });
      }
      return cache.writeQuery({
        query: GET_USER_RATING,
        variables: {
          content_id: contentId,
        },
        data: {
          UserRecipeFeedback: {
            __typename: 'UserRecipeFeedback',
            id: UserRecipeFeedback.id,
            star_rating: UserRecipeFeedback.star_rating,
          },
        },
      });
    },
  });

  const userRating = get(data, 'UserRecipeFeedback.star_rating', null);
  const isEditMode = isActive || ratingWasSaved || saveInProgress;
  let WrapperElement;
  if (readOnly) {
    WrapperElement = 'div';
  } else {
    WrapperElement = isEditMode ? 'form' : 'button';
  }
  const wrapperClassName = classNames(
    'star-rating',
    { 'star-rating--compact': compact },
    { 'star-rating--editing': isEditMode },
    className,
  );

  const isKeyboardEvent = event =>
    event &&
    event.nativeEvent &&
    event.nativeEvent.clientX === 0 &&
    event.nativeEvent.clientY === 0;

  const handleRatingChange = (event, star_rating) => {
    if (!loggedIn) {
      history.push('/subscribe', {
        returnTo: history.location,
        signupMessage: 'Join ckbk free to rate this recipe',
      });
      return;
    }

    cancelTransitionToAggregateView();

    const newOrExistingRating = star_rating === userRating ? 0 : star_rating;
    // Using the arrow keys causes this handler to fire. This causes a click event with clientX=0,clientY=0.
    // We don't want to save until the user presses enter - in that case this handler will be called again with the
    // submit event, which will pass the following check.
    if (newOrExistingRating === 0 && currentUserComment) {
      return;
    }

    if (!isKeyboardEvent(event)) {
      setHover(false);
      saveRating({
        variables: {
          star_rating: newOrExistingRating,
          content_id: contentId,
          content_hash: contentHash,
        },
        optimisticResponse: {
          UserRecipeFeedback: {
            id: get(data, 'UserRecipeFeedback.id', 'temp-id'),
            __typename: 'UserRecipeFeedback',
            star_rating: newOrExistingRating,
            ratings: { ...aggregateRating, __typename: 'RatingInfo' },
          },
        },
      });
    }
  };

  const handleRatingChangeActive = (event, value) => {
    setUnsavedValue(value);
    cancelTransitionToAggregateView();
  };

  const handlePointerEnter = evt => {
    if (!readOnly && evt.target.tagName !== 'LABEL') {
      setHover(true);
      cancelTransitionToAggregateView();
    }
  };

  const handlePointerLeave = () => {
    if (!readOnly) {
      setHover(false);
      if (ratingWasSaved) {
        beginTransitionToAggregateView(true);
      }
    }
  };

  const handleClick = event => {
    if (!readOnly) {
      if (isTouch.current !== true && !isEditMode) {
        // Shortcut for mice - if they click on the inactive component we should take that as them setting a rating
        // The MuiRating component is not active (we don't want the hover effect) so we'll need to work out which
        // star was under the pointer and enqueue the rating change handler ourself.
        const rect = event.target.getBoundingClientRect();
        if (rect.width) {
          const x = event.nativeEvent.clientX - rect.left; // x position within the element.
          const clickedStar = Math.round(0.5 + x * (5 / rect.width));

          if (clickedStar > 0 && clickedStar <= 5) {
            // Avoid overlapping event handlers
            setTimeout(() => handleRatingChange(event, clickedStar), 0);
          }
        }
      } else {
        // Touch and keyboards - just make the Mui Rating component active.
        // It will call handleRatingChange when the user selects a rating.
        setActive(true);
      }
    }
    setRatingWasSaved(false);
    if (isKeyboardEvent(event) && event.target.tagName === 'BUTTON') {
      setActive(true);
      // After the render focus the current star rating
      setTimeout(() => {
        if (ratingsComponentRef.current) {
          let starElement = ratingsComponentRef.current.querySelector(':checked');
          if (!starElement) {
            starElement = ratingsComponentRef.current.querySelector('input');
          }
          if (starElement) {
            starElement.focus();
          }
        }
      }, 0);
    }

    isTouch.current = false;
  };

  const handleTouchStart = () => {
    // Set the isTouch ref to true whenever we see a touchstart event, and then to false upon completion of the click event handler.
    // We can use it to detect whether the click (or onmouseover) was triggered by a touch.
    isTouch.current = true;
  };

  const handleSubmit = event => {
    event.preventDefault();
    handleRatingChange(event, unsavedValue);
    setUnsavedValue(null);
  };

  const value = isEditMode || hover ? userRating : aggregateRating.average;

  const YourRatingButton = () => {
    const handleMouseEnter = () => {
      setHover(true);
    };

    const handleMouseLeave = () => {
      setHover(false);
    };
    return (
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="star-rating__your-rating"
      >
        <span>Your rating</span>
        {
          !currentUserComment ? <span className="remove-rating-button" onClick={e => handleRatingChange(e, userRating)}>
          <CloseIcon fontSize="inherit"/>
        </span> : null
        }
      </span>
    );
  };

  let text = null;
  if (!noText) {
    if (saveInProgress) {
      text = 'Saving...';
    } else if (ratingWasSaved) {
      text = userRating ? 'Rating saved' : 'Rating removed';
    } else if ((hover || isActive) && !value && !readOnly) {
      text = 'Rate this recipe';
    } else if (hover) {
      text = <YourRatingButton />;
    } else if (compact) {
      text = `(${count || 0})`;
    } else {
      text = `${aggregateRating.count || 0} rating${aggregateRating.count !== 1 ? 's' : ''}`;
    }
  }

  return (
    <WrapperElement
      className={wrapperClassName}
      onClick={handleClick}
      onMouseOver={handlePointerEnter}
      onMouseOut={handlePointerLeave}
      onTouchStart={handleTouchStart}
      onSubmit={handleSubmit}
      onFocus={handlePointerEnter}
      onBlur={handlePointerLeave}
      tabIndex={readOnly ? null : 0}
    >
      <Rating
        ref={ratingsComponentRef}
        value={value || 0}
        name="star-rating"
        precision={isEditMode || hover ? 1 : 0.5}
        size={size}
        onChange={handleRatingChange}
        onChangeActive={handleRatingChangeActive}
        classes={{
          iconActive: 'star-rating__stars--user',
          root: isEditMode || hover ? 'star-rating__stars--user' : 'star-rating__stars--average',
        }}
        readOnly={readOnly || !isActive}
      />
      <p className="star-rating__text">{text}</p>
      {readOnly ? null : <input type="submit" hidden />}
    </WrapperElement>
  );
}

StarRating.propTypes = {
  averageRating: PropTypes.number,
  className: PropTypes.string,
  compact: PropTypes.bool,
  contentHash: PropTypes.string,
  contentId: PropTypes.string.isRequired,
  count: PropTypes.number,
  noText: PropTypes.bool,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  currentUserComment: PropTypes.object,
};

StarRating.defaultProps = {
  averageRating: null,
  className: null,
  currentUserComment: null,
  compact: false,
  contentHash: null,
  count: 0,
  noText: false,
  readOnly: false,
  size: 'large',
};

export default React.memo(StarRating);
